import { DateTime } from '@ksyos/date-time';
import {
    Avatar,
    Badge,
    Button,
    Card,
    Collapse,
    Collection,
    CollectionItem,
    CollectionLink,
    Link,
} from '@ksyos/design-system';
import { Link as ReactRouterLink } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IEmployment } from '@frontend/components/interfaces/employment';
import type { IReduxState } from '@frontend/configureStore';
import { ManagerAuthorization } from '@common/enums';
import { isActiveOnDate } from '@common/utils/isActiveOnDate';
import { translateKey } from '@frontend/translate';
import { hasOneAuthorization, useAbortableFetch } from '@frontend/utils';

interface IEmploymentListProps {
    practitionerId: string;
}

export function EmploymentList(props: IEmploymentListProps) {
    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);

    const [activeEmployments, setActiveEmployments] = useState<IEmployment[]>([]);
    const [inactiveEmployments, setInactiveEmployments] = useState<IEmployment[]>([]);
    const [hasKsyosEmployment, setHasKsyosEmployment] = useState<boolean>(false);
    const [showInactiveEmployments, setShowInactiveEmployments] = useState<boolean>(false);
    const abortableFetch = useAbortableFetch();

    useEffect(() => {
        loadEmployments();
    }, [props.practitionerId]);

    if (!isManager || (activeEmployments.length === 0 && inactiveEmployments.length === 0)) {
        return null;
    }

    if (!inactiveEmployments.length) {
        return renderCard(activeEmployments);
    }
    if (!activeEmployments.length) {
        return renderCard(inactiveEmployments);
    }

    return (
        <Card>
            <Collection
                aria-label={translateKey('organization.organizationList')}
                type="ul"
            >
                {renderEmploymentAddButtonHeader()}
                {activeEmployments.map((employment) => renderEmployment(employment))}
                <Collapse isOpen={showInactiveEmployments}>
                    {inactiveEmployments.map((employment) => renderEmployment(employment))}
                </Collapse>
            </Collection>
            <Card.Footer>
                <Link
                    color="minimal"
                    fontWeight="normal"
                    iconLeft={showInactiveEmployments ? 'caret-up' : 'caret-down'}
                    onClick={() => setShowInactiveEmployments(!showInactiveEmployments)}
                    size="sm"
                    variant="button"
                >
                    {showInactiveEmployments
                        ? translateKey('organization.foldInactiveOrganization')
                        : translateKey('organization.showInactiveOrganization')}
                </Link>
            </Card.Footer>
        </Card>
    );

    function renderCard(employments: IEmployment[]) {
        return (
            <Card>
                {renderEmploymentAddButtonHeader()}
                <Collection>{employments.map((employment) => renderEmployment(employment))}</Collection>
            </Card>
        );
    }

    function renderEmployment(employment: IEmployment) {
        const activeText = getActiveText(employment);

        const collectionProps = {
            key: employment.id,
            mediaItem: (
                <Avatar
                    icon="organization"
                    shape="square"
                    size={56}
                />
            ),
            title: employment.organization!.name,
            subtitle: employment.organization?.txcsId,
        };

        return hasOneAuthorization(ManagerAuthorization.employmentSettingsRead) ? (
            <CollectionLink
                {...collectionProps}
                as={ReactRouterLink}
                mediaItem={
                    <Avatar
                        icon="organization"
                        shape="square"
                        size={56}
                    />
                }
                subtitle={employment.organization?.txcsId}
                title={`${employment.organization!.name}`}
                to={`/employments/${employment.id}/personalSettings`}
            >
                <Badge text={activeText} />
            </CollectionLink>
        ) : (
            <CollectionItem {...collectionProps}>
                <Badge text={activeText} />
            </CollectionItem>
        );
    }

    function renderEmploymentAddButtonHeader() {
        return (
            <Card.Header
                action={
                    hasOneAuthorization(ManagerAuthorization.practitionerAddEdit) &&
                    !hasKsyosEmployment && (
                        <Button
                            as={ReactRouterLink}
                            iconLeft="add"
                            to={`/practitioners/${props.practitionerId}/employments/add`}
                        >
                            {translateKey('organization.add')}
                        </Button>
                    )
                }
                title={translateKey('organization.organizations')}
            />
        );
    }

    function getActiveText(employment: IEmployment) {
        let activeText = `${translateKey('employment.activeFrom')} ${DateTime.from(employment.startDate).format(
            'DD-MM-YYYY'
        )}`;
        if (employment.endDate) {
            activeText += ` ${translateKey('common.until')} ${DateTime.from(employment.endDate).format('DD-MM-YYYY')}`;
        }

        return activeText;
    }

    function filterByIsNotKsyosEmployment(allEmployments: IEmployment[]) {
        return allEmployments.filter((item) => !item.organization?.isKsyos);
    }

    function filterByActive(allEmploymentsKsyosFiltered: IEmployment[]) {
        const today = new Date();
        const activeEmploymentsFiltered = allEmploymentsKsyosFiltered
            .filter((item) => {
                return isActiveOnDate(item, today) && item.organization?.isActive;
            })
            .sort((a, b) => DateTime.from(b.startDate).diff(DateTime.from(a.startDate)));

        return activeEmploymentsFiltered;
    }

    function filterByInActive(allEmploymentsKsyosFiltered: IEmployment[]) {
        const today = new Date();
        const inActiveEmploymentsFiltered = allEmploymentsKsyosFiltered
            .filter((item) => {
                return !isActiveOnDate(item, today) || !item.organization?.isActive;
            })
            .sort((a, b) => DateTime.from(b.startDate).diff(DateTime.from(a.startDate)));

        return inActiveEmploymentsFiltered;
    }

    async function loadEmployments() {
        const url = `/api/employments/${props.practitionerId}/organizations`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        const allEmployments = result.json.items as IEmployment[];
        setHasKsyosEmployment(allEmployments.find((item) => item.organization?.isKsyos) !== undefined);
        const allEmploymentsKsyosFiltered = filterByIsNotKsyosEmployment(allEmployments);
        setInactiveEmployments(filterByInActive(allEmploymentsKsyosFiltered));
        setActiveEmployments(filterByActive(allEmploymentsKsyosFiltered));
    }
}
